import type { TFunction } from 'i18next'

import { ApiError, type ErrorSubject } from './api'

/**
 * The error-code machinery every form shares, extracted when the carrier forms became
 * its third consumer. Each feature brings its own field map — the domain names the
 * rule that broke, the form knows which box that rule belongs to — and the mechanics
 * of sorting, clearing and translating live here once.
 *
 * Codes only, no `t` in sight until render: a message resolved at failure time would
 * be frozen in whatever language was active when the save failed.
 */

export interface FormErrors {
  /** The code complaining about each field, for the box it belongs to. */
  readonly fields: Record<string, string>
  /** Codes that belong to no single field, shown above the form. */
  readonly summary: string[]
  /**
   * The record a code is about, when the API named one — the holder of a number this
   * form typed (TRKC-83) — keyed by code, so the sentence can name it and link to it.
   */
  readonly subjects?: Readonly<Record<string, ErrorSubject>>
}

/**
 * A rejection with no code of its own — a network failure, or a response that carried
 * none. Translated as the caller's generic message.
 */
export const GENERIC_ERROR = ''

/**
 * The refusal of an edit made on a reading somebody else's save has since replaced.
 * Belongs to no field — it is about the whole record — so it lands in the summary, and
 * every form that edits a record answers it the same way: keep what was typed, and
 * offer to load what is saved.
 */
export const CHANGED_ELSEWHERE = 'record_changed_elsewhere'

/**
 * Sorts a failed save into the boxes its complaints belong to. Codes the map does not
 * recognise go to the summary, so a rule added on the server before the portal caught
 * up is still reported rather than swallowed.
 */
export function toFormErrors(
  caught: unknown,
  fieldByCode: Record<string, string>,
): FormErrors {
  const fields: Record<string, string> = {}
  const summary: string[] = []

  if (!(caught instanceof ApiError) || caught.codes.length === 0) {
    return { fields, summary: [GENERIC_ERROR] }
  }

  for (const code of caught.codes) {
    const field = fieldByCode[code]

    if (field === undefined) {
      if (!summary.includes(code)) {
        summary.push(code)
      }
    } else {
      fields[field] = code
    }
  }

  return { fields, summary, subjects: caught.subjects }
}

/**
 * Drops what no longer applies once a field has been edited: its own complaint, and
 * any cross-field one this field is party to (`clearedBy` lists the fields whose edit
 * clears a given code — the ZIP-versus-state pairing being the founding example).
 */
export function withoutErrorsFor(
  errors: FormErrors,
  editedField: string,
  clearedBy: Record<string, readonly string[]> = {},
): FormErrors {
  const remaining = Object.entries(errors.fields).filter(([field, code]) => {
    if (field === editedField) {
      return false
    }

    return !(clearedBy[code]?.includes(editedField) ?? false)
  })

  return remaining.length === Object.keys(errors.fields).length
    ? errors
    : { ...errors, fields: Object.fromEntries(remaining) }
}

/**
 * The sentence for one code, in whatever language is active right now. An
 * unrecognised code falls back to the caller's generic message: less specific, still
 * translated, never the server's English.
 *
 * A sentence about another record names it: the subject's name fills `{{name}}`, and a
 * code that names nobody gets "another record" so no placeholder ever reaches the
 * screen (i18next leaves an unfilled one in place).
 */
export function messageFor(
  code: string,
  t: TFunction,
  fallbackKey: string,
  subject?: ErrorSubject,
): string {
  if (code === GENERIC_ERROR) {
    return t(fallbackKey)
  }

  const key = `errors.${code}`
  const translated = t(key, { name: subject?.name ?? t('registry.anotherRecord') })

  // i18next echoes the key back when it has no translation for it.
  return translated === key ? t(fallbackKey) : translated
}
